import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import useSWR from 'swr'
import { fetchVodAsset, updateVodAsset } from '../services/vodApi'
import { fetchMatches } from '../services/api'
import { Button, Panel, Badge } from '../components'
import { useToast } from '../contexts/ToastContext'
import { formatToPacificTime } from '../utils/timezone'
import { VodAsset, VodRendition, VodOrigin } from '../types/vod'
import pageStyles from '../styles/page.module.scss'
import styles from './VodDetail.module.scss'

const DEFAULT_EDITABLE = ['tc_title', 'tc_description', 'tc_synopsis']

// Whether this browser can play HLS natively (Safari). Without a JS player
// (e.g. hls.js) other browsers can't preview HLS inline.
const NATIVE_HLS =
  typeof document !== 'undefined' &&
  !!document.createElement('video').canPlayType('application/vnd.apple.mpegurl')

const ORIGIN_VARIANT: Record<VodOrigin, 'primary' | 'secondary' | 'neutral'> = {
  AMAGI: 'secondary',
  WSC: 'secondary',
  TENNIS_CHANNEL: 'primary',
  VIRTUAL: 'neutral',
  UNKNOWN: 'neutral',
}

const AVAILABILITY_VARIANT: Record<
  string,
  'success' | 'warning' | 'error' | 'neutral'
> = {
  AVAILABLE: 'success',
  UPCOMING: 'warning',
  EXPIRED: 'error',
  UNKNOWN: 'neutral',
}

interface PreviewOption {
  id: string
  kind: string
  url: string
  native: boolean
}

// One preview option per playable rendition kind (MP4 / HLS / DASH) that has a
// URL. `native` = the browser can play it inline without a JS player.
function previewOptions(asset: VodAsset): PreviewOption[] {
  const opts: PreviewOption[] = []
  for (const r of asset.renditions || []) {
    if (!r.cdn_url) continue
    if (r.rendition_kind_code === 'HLS') {
      opts.push({ id: r.tc_vod_rendition_id, kind: 'HLS', url: r.cdn_url, native: NATIVE_HLS })
    } else if (r.rendition_kind_code === 'DASH') {
      opts.push({ id: r.tc_vod_rendition_id, kind: 'DASH', url: r.cdn_url, native: false })
    } else if (r.rendition_kind_code === 'MP4') {
      opts.push({ id: r.tc_vod_rendition_id, kind: 'MP4', url: r.cdn_url, native: true })
    }
  }
  // Legacy MP4-shaped video field, if no MP4 rendition row exists
  if (!opts.some((o) => o.kind === 'MP4') && asset.video?.cdn_url) {
    opts.push({ id: 'legacy-mp4', kind: 'MP4', url: asset.video.cdn_url, native: true })
  }
  const order: Record<string, number> = { MP4: 0, HLS: 1, DASH: 2 }
  return opts.sort((a, b) => (order[a.kind] ?? 9) - (order[b.kind] ?? 9))
}

const VodDetail: React.FC = () => {
  const { vodId } = useParams<{ vodId: string }>()
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [synopsis, setSynopsis] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [selectedPreviewIdx, setSelectedPreviewIdx] = useState(0)

  const { data: asset, isLoading, error, mutate } = useSWR(
    vodId ? ['vod-asset-detail', vodId] : null,
    () => fetchVodAsset(vodId!),
    { revalidateOnFocus: false }
  )

  // Fetch full match data if linked (same approach as the highlights detail)
  const matchTcId = asset?.match?.tc_match_id
  const matchStartTime = asset?.match?.tc_start_time
  const { data: matchResponse, isLoading: matchLoading } = useSWR(
    matchTcId ? ['vod-detail-match', matchTcId, matchStartTime] : null,
    async () => {
      if (!matchTcId) return null
      let startDate: string
      let endDate: string
      if (matchStartTime) {
        const date = new Date(matchStartTime)
        const start = new Date(date)
        start.setDate(start.getDate() - 30)
        const end = new Date(date)
        end.setDate(end.getDate() + 30)
        startDate = start.toISOString().split('T')[0]
        endDate = end.toISOString().split('T')[0]
      } else {
        const today = new Date()
        const start = new Date(today)
        start.setFullYear(start.getFullYear() - 1)
        const end = new Date(today)
        end.setFullYear(end.getFullYear() + 1)
        startDate = start.toISOString().split('T')[0]
        endDate = end.toISOString().split('T')[0]
      }
      const response = await fetchMatches({
        start_date: startDate,
        end_date: endDate,
        search: matchTcId,
        limit: 1,
        page: 1,
      })
      return response.matches?.[0] || null
    },
    { revalidateOnFocus: false }
  )
  const match = matchResponse || null

  useEffect(() => {
    if (asset) {
      setTitle(asset.tc_title || '')
      setDescription(asset.tc_description || '')
      setSynopsis(asset.tc_synopsis || '')
      setPreviewFailed(false)
      setSelectedPreviewIdx(0)
    }
  }, [asset])

  const editableFields = asset?.capabilities?.editable_fields ?? DEFAULT_EDITABLE
  const canEdit = (field: string) => editableFields.includes(field)

  const hasChanges = useMemo(() => {
    if (!asset) return false
    return (
      title !== (asset.tc_title || '') ||
      description !== (asset.tc_description || '') ||
      synopsis !== (asset.tc_synopsis || '')
    )
  }, [title, description, synopsis, asset])

  const titleValid = !canEdit('tc_title') || title.trim().length > 0

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    const mm = mins.toString().padStart(hrs ? 2 : 1, '0')
    const ss = secs.toString().padStart(2, '0')
    return hrs ? `${hrs}:${mm}:${ss}` : `${mm}:${ss}`
  }

  const handleSave = async () => {
    if (!vodId || !asset) return
    if (!titleValid) {
      toast('Title is required', 'error')
      return
    }
    setIsSaving(true)
    try {
      const payload: {
        tc_title?: string
        tc_description?: string
        tc_synopsis?: string
      } = {}
      if (canEdit('tc_title')) payload.tc_title = title.trim()
      if (canEdit('tc_description')) payload.tc_description = description.trim()
      if (canEdit('tc_synopsis')) payload.tc_synopsis = synopsis.trim()
      const res = await updateVodAsset(vodId, payload)
      // Cache the authoritative asset from the response (not the wrapper)
      await mutate(res.asset, { revalidate: false })
      toast('VOD updated', 'success')
    } catch (err) {
      toast((err as Error).message || 'Failed to save changes', 'error')
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    if (!asset) return
    setTitle(asset.tc_title || '')
    setDescription(asset.tc_description || '')
    setSynopsis(asset.tc_synopsis || '')
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading VOD</h2>
          <p>Failed to fetch VOD details: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  if (isLoading || !asset) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.loading}>
          <h3>Loading VOD...</h3>
          <p>Please wait while we fetch the VOD data.</p>
        </div>
      </div>
    )
  }

  const options = previewOptions(asset)
  const selectedIdx = selectedPreviewIdx < options.length ? selectedPreviewIdx : 0
  const selected = options[selectedIdx] || null
  const renditions: VodRendition[] = asset.renditions || []
  const availWindow =
    asset.avail_start || asset.avail_end
      ? `${asset.avail_start ? formatToPacificTime(asset.avail_start) : '—'} → ${
          asset.avail_end ? formatToPacificTime(asset.avail_end) : '—'
        }`
      : 'No window set'

  return (
    <div>
      <div className={pageStyles.pageHeader}>
        <h1>VOD Details</h1>
        <p>
          <code className={styles.idCode}>{asset.tc_vod_asset_id}</code>
        </p>
      </div>

      <div className={styles.detailContent}>
        {/* Left: VOD Metadata (editable + read-only, mirrors highlights) */}
        <div className={styles.leftColumn}>
          <Panel>
            <Panel.Header>
              <h2>VOD Metadata</h2>
            </Panel.Header>
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <label>
                  Title{' '}
                  {canEdit('tc_title') && (
                    <span className={styles.required}>*</span>
                  )}
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={!canEdit('tc_title')}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={!canEdit('tc_description')}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Synopsis</label>
                <textarea
                  value={synopsis}
                  onChange={(e) => setSynopsis(e.target.value)}
                  disabled={!canEdit('tc_synopsis')}
                  className={styles.textarea}
                  rows={3}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Asset ID</label>
                <input
                  type="text"
                  value={asset.tc_vod_asset_id}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Type</label>
                <input
                  type="text"
                  value={asset.vod_type_display_name || asset.vod_type_code}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Source</label>
                <div>
                  <Badge variant={ORIGIN_VARIANT[asset.origin || 'UNKNOWN']}>
                    {asset.origin || 'UNKNOWN'}
                  </Badge>
                  {asset.source_vendor &&
                  asset.source_vendor.toUpperCase() !== (asset.origin || '')
                    ? ` (${asset.source_vendor})`
                    : ''}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <label>
                  Window{' '}
                  <Badge
                    variant={
                      AVAILABILITY_VARIANT[asset.availability_state || 'UNKNOWN']
                    }
                  >
                    {asset.availability_state || 'UNKNOWN'}
                  </Badge>
                </label>
                <input
                  type="text"
                  value={availWindow}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Duration</label>
                <input
                  type="text"
                  value={formatDuration(asset.duration_sec)}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Requires subscription</label>
                <input
                  type="text"
                  value={
                    asset.requires_subscription == null
                      ? '—'
                      : asset.requires_subscription
                        ? 'Yes'
                        : 'No'
                  }
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Created At</label>
                <input
                  type="text"
                  value={formatToPacificTime(asset.created_at)}
                  disabled
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label>Updated At</label>
                <input
                  type="text"
                  value={formatToPacificTime(asset.updated_at)}
                  disabled
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.actionButtons}>
              <Button
                onClick={handleSave}
                size="large"
                disabled={isSaving || !hasChanges || !titleValid}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
              {hasChanges && (
                <Button
                  variant="secondary"
                  size="large"
                  onClick={handleReset}
                  disabled={isSaving}
                >
                  Reset Changes
                </Button>
              )}
            </div>
          </Panel>
        </div>

        {/* Right: preview (top), related match, renditions */}
        <div className={styles.rightColumn}>
          <Panel margin="none">
            <Panel.Header>
              <h2>Preview</h2>
            </Panel.Header>
            {options.length === 0 ? (
              <div className={styles.previewFallback}>
                <p>No previewable rendition available for this asset.</p>
              </div>
            ) : (
              <>
                {options.length > 1 && (
                  <div className={styles.previewTabs}>
                    {options.map((o, i) => (
                      <button
                        key={o.id}
                        type="button"
                        className={
                          i === selectedIdx
                            ? `${styles.previewTab} ${styles.previewTabActive}`
                            : styles.previewTab
                        }
                        onClick={() => {
                          setSelectedPreviewIdx(i)
                          setPreviewFailed(false)
                        }}
                      >
                        {o.kind}
                      </button>
                    ))}
                  </div>
                )}
                {selected && selected.native && !previewFailed ? (
                  <video
                    key={selected.url}
                    className={styles.player}
                    src={selected.url}
                    controls
                    preload="metadata"
                    onError={() => setPreviewFailed(true)}
                  >
                    Your browser does not support inline video.
                  </video>
                ) : selected ? (
                  <div className={styles.previewFallback}>
                    <p>
                      {selected.kind === 'HLS'
                        ? "This browser can't play HLS inline (Safari can, without a JS player)."
                        : 'DASH needs a JS player (dash.js/Shaka) — no browser plays it inline natively.'}
                    </p>
                    <p className={styles.previewHint}>
                      This is a player limitation, not a sign the manifest is
                      broken. Open it to inspect, or verify playback in a{' '}
                      {selected.kind}-capable player.
                    </p>
                    <a href={selected.url} target="_blank" rel="noopener noreferrer">
                      Open {selected.kind} manifest ↗
                    </a>
                  </div>
                ) : null}
              </>
            )}
          </Panel>

          {/* Related Match — identical to Highlight & Condensed Match Details */}
          {matchTcId && (
            <Panel margin="none">
              <Panel.Header>
                <h2>Related Match</h2>
              </Panel.Header>
              {matchLoading ? (
                <div className={styles.loadingState}>
                  <p>Loading match data...</p>
                </div>
              ) : match ? (
                <div className={styles.matchInfo}>
                  <div className={styles.matchRow}>
                    <strong>Players:</strong>
                    <span>
                      {match.home_competitor?.name || 'TBD'} vs{' '}
                      {match.away_competitor?.name || 'TBD'}
                    </span>
                  </div>
                  {match.competition && (
                    <div className={styles.matchRow}>
                      <strong>Tournament:</strong>
                      <span>{match.competition.name}</span>
                    </div>
                  )}
                  {match.match.effective_round ? (
                    <div className={styles.matchRow}>
                      <strong>Round:</strong>
                      <span>{match.match.effective_round}</span>
                    </div>
                  ) : null}
                  {match.match.effective_start_time ? (
                    <div className={styles.matchRow}>
                      <strong>Match Date/Time:</strong>
                      <span>
                        {formatToPacificTime(match.match.effective_start_time)}
                      </span>
                    </div>
                  ) : null}
                  {match.match.effective_status ? (
                    <div className={styles.matchRow}>
                      <strong>Match Status:</strong>
                      <span>{match.match.effective_status}</span>
                    </div>
                  ) : null}
                  {match.match.tc_match_id && (
                    <div className={styles.matchRow}>
                      <Link
                        to={`/matches/${match.match.tc_match_id}`}
                        className={styles.matchLink}
                      >
                        View Match Details →
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.errorState}>
                  <p>
                    Match not found for SR ID:{' '}
                    <code>{asset?.match?.ext_event_id ?? 'N/A'}</code> or TC ID:{' '}
                    <code>{matchTcId ?? 'N/A'}</code>
                  </p>
                  <p className={styles.errorHint}>
                    Match data may not be available yet, or SR ID may be invalid.
                  </p>
                </div>
              )}
            </Panel>
          )}

          <Panel margin="none">
            <Panel.Header>
              <h2>Renditions</h2>
            </Panel.Header>
            {renditions.length === 0 ? (
              <div className={styles.emptyText}>No renditions.</div>
            ) : (
              <div className={styles.renditions}>
                {renditions.map((r) => (
                  <div key={r.tc_vod_rendition_id} className={styles.renditionRow}>
                    <div className={styles.renditionKind}>
                      <strong>
                        {r.rendition_kind_display_name || r.rendition_kind_code}
                      </strong>
                      {r.is_playable ? (
                        <Badge variant="success">Playable</Badge>
                      ) : (
                        <Badge variant="neutral">Not playable</Badge>
                      )}
                    </div>
                    {r.s3_bucket && (
                      <div className={styles.assetRow}>
                        <strong>Bucket:</strong>
                        <code className={styles.codeValue}>{r.s3_bucket}</code>
                      </div>
                    )}
                    {r.s3_key && (
                      <div className={styles.assetRow}>
                        <strong>Key:</strong>
                        <code className={styles.codeValue}>{r.s3_key}</code>
                      </div>
                    )}
                    {r.mime_type && (
                      <div className={styles.assetRow}>
                        <strong>MIME Type:</strong>
                        <span>{r.mime_type}</span>
                      </div>
                    )}
                    {r.cdn_url && (
                      <div className={styles.assetRow}>
                        <strong>URL:</strong>
                        <a
                          href={r.cdn_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.urlLink}
                        >
                          {r.cdn_url}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  )
}

export default VodDetail
